import React from 'react'
import { ThreeDCardPreview } from '@sendoutcards/editor'
import {
  Drawer,
  DrawerProps,
  Flex,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { useRef } from 'react'
import { ShareStatusType } from 'src/graphql/generated/graphql'
import {
  mapPanelControlNames,
  mapPanelImages,
} from 'src/helpers/threeDCard/mapPanelImages'
import {
  useActions,
  useEffect,
  usePersistedUserData,
  useQueryParams,
  useSelector,
  useState,
} from 'src/hooks'
import { RecipientView } from '../RecipientView/RecipientView'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import ActRequestCard from '../ActRequestCard/ActRequestCard'
import {
  useGetActRecipientActionScreenContent,
  useUpdateDigitalCardStatus,
} from 'src/react_query'
import {
  useDigitalGiftCardProduct,
  useFlags,
  useGetDigitalCardByToken,
} from 'src/react_query/queries/hooks'
import { ActSendFreeCardDialog } from '../FreeCardSendModal/ActSendFreeCardDialog'
import { useSocPublicImageBucketUrl } from 'src/hooks/useSocPublicImageBucketUrl'
import { UpsaleDialog } from 'src/upsale/components/UpsaleDialog'
import { persistSponsorData } from 'src/hooks/useQuerySponsor'
import { getIsBackPanelLandscape } from 'src/helpers/getCardBackPanelOrientation'
import { BottomSheet } from 'react-spring-bottom-sheet'
import 'react-spring-bottom-sheet/dist/style.css'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import { GiftCardRedemption } from '../GiftCardRedemption/GiftCardRedemption'

type ViewActProps = {
  token: string
}

const ViewAct: React.FC<ViewActProps> = props => {
  const { token } = props
  const { data: digitalCard } = useGetDigitalCardByToken(
    { token: token },
    { suspense: true },
  )
  if (!digitalCard) {
    throw new Error('Failed to load ACT')
  }

  const { account } = usePersistedUserData()
  const { mutate: updateDigitalCardStatus } = useUpdateDigitalCardStatus()
  const flags = useFlags()
  const canViewEnvelopeAnimation = flags.data?.some(
    flag => flag.name === 'can_view_envelope_animation',
  )

  const { data: recipientActionScreenContent } =
    useGetActRecipientActionScreenContent()
  const giftCard = digitalCard.recipients[0]?.giftCards.at(0)
  const digitalGiftCardProduct = useDigitalGiftCardProduct({
    code: giftCard?.productCodes[0] ?? '',
  })
  const product = digitalGiftCardProduct.data

  const freeCardUpsaleContent =
    recipientActionScreenContent?.freeCardUpsaleContent
  const leftDrawerRef = useRef<HTMLDivElement>(null)
  const isDesktop = useSelector(state => state.window.width >= 961)
  const controlContainerRef = useRef<HTMLDivElement>(null)
  const [shouldPortalControls, setShouldPortalControls] = useState(false)
  const [shouldShowRequestCard, setShouldShowRequestCard] = useState(false)
  const [shouldShowFreeCardUpsale, setShouldShowFreeCardUpsale] =
    useState(false)
  const [shouldShowSendFreeCardFlow, setShouldShowSendFreeCardFlow] =
    useState(false)
  const [isSponsorStored, setIsSponsorStored] = useState(false)

  const {
    redeem: shouldShowRedeemCode,
    insertQueryParams,
    removeQueryParams,
  } = useQueryParams()
  const setShouldShowRedeemCode = (newValue: boolean) => {
    if (newValue) {
      insertQueryParams({ redeem: '' })
    } else {
      removeQueryParams(['redeem'])
    }
  }

  const controlPanelNames = mapPanelControlNames(digitalCard.card.panels)

  const actions = useActions()

  const { isRecipientDrawerOpen } = useSelector(state => state.act)

  const giftCardPreview = digitalGiftCardProduct.data
    ? {
        title: digitalGiftCardProduct.data.title,
        imageUrl: digitalGiftCardProduct.data?.image ?? '',
        value: `$${giftCard?.amount}`,
      }
    : undefined

  const redeemCodeContent = (
    <div>
      <GiftCardRedemption
        giftCardImage={product?.image ?? undefined}
        giftCardTitle={product?.title ?? undefined}
        giftCardFaceValue={giftCard?.amount}
        giftCardCurrency={giftCard?.currency}
        token={token}
        allowedPhoneNumber={
          giftCard?.linkedPhoneNumber
            ? {
                countryIsoCode: giftCard.linkedPhoneNumber.country.isoCode,
                nationalNumber: giftCard.linkedPhoneNumber.nationalNumber,
              }
            : undefined
        }
        onClose={() => {
          setShouldShowRedeemCode(false)
        }}
      />
    </div>
  )

  const recipientDrawerContent = (
    <RecipientView
      title={digitalCard.shareMessageTitle}
      sender={{
        firstname:
          digitalCard.recipients[0].sender.firstName ||
          digitalCard.sender.firstName,
        lastname:
          digitalCard.recipients[0].sender.lastName ||
          digitalCard.sender.lastName,
        imageUrl: digitalCard.sender.storefront?.mugshot?.url,
      }}
      giftCards={digitalCard.recipients[0]?.giftCards ?? []}
      primaryAction={{
        title: 'Try it yourself',
        onClick: () => setShouldShowFreeCardUpsale(true),
      }}
      secondaryAction={{
        onClick: () => {
          actions.setRecipientDrawerOpen(false)
          setShouldShowRequestCard(true)
        },
      }}
      redeemGiftAction={{
        title: giftCard?.linkedPhoneNumber ? 'View Gift' : 'Claim Gift',
        onClick: () => setShouldShowRedeemCode(true),
      }}
    />
  )

  useEffect(() => {
    if (controlContainerRef.current) {
      setShouldPortalControls(true)
    }
  }, [])

  useEffect(() => {
    if (isSponsorStored) return
    persistSponsorData(digitalCard.sponsor.genealogyId.toString())
    setIsSponsorStored(true)
  }, [digitalCard.sponsor, isSponsorStored])

  useEffect(() => {
    const senderId = digitalCard.sender.id
    const recieverId = account?.id

    const existingShareStatus = digitalCard.recipients.find(
      rec => rec.token === token,
    )?.shareStatus

    // If viewing party is not the sender
    if (!recieverId || recieverId !== senderId) {
      // And existing share status is not VIEWED
      if (existingShareStatus && existingShareStatus !== 'VIEWED') {
        // We set it to viewed
        updateDigitalCardStatus({
          token: token,
          status: ShareStatusType.Viewed,
        })
      }
    }
  }, [digitalCard, account?.id, updateDigitalCardStatus, token])

  const containerStyles = {
    height: isDesktop ? '100dvh' : 'calc(100dvh - 65px)',
    width: '100dvw',
    position: 'fixed',
    top: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FEFEFE',
  } as const

  const drawerProps: DrawerProps = {
    isTabHidden: false,
    zIndex: 10,
    location: 'left',
    offset: 0,
    openValue: 350,
    isOpen: isRecipientDrawerOpen,
    onClick: () => actions.setRecipientDrawerOpen(!isRecipientDrawerOpen),
    overflow: 'hidden',
    tabMode: 'dark',
  }

  const freeCardHeroImage = useSocPublicImageBucketUrl('freecardhero.png')

  return (
    <div style={{ ...containerStyles }}>
      <style>
        {`
          .osano-cm-dialog {
            background-color: #fff;
            font-size: 12px;
            display: flex;
            flex-direction: row;
            color: #555;
            padding: 0.25rem 0.75rem;
            column-gap: 0.5rem;
            box-shadow: 0 4px 10px 0 #bdbaba57;
            overflow: hidden;
          }

          .osano-cm-buttons {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            column-gap: 0.25rem;
          }

          .osano-cm-dialog--type_bar .osano-cm-button, .osano-cm-button {
            flex: 1 1 auto;
            max-height: 64px;
            aspect-ratio: 1 / 1;
            border-radius: 12px;
            border-style: none;
          }
          `}
      </style>
      {isDesktop && (
        <Drawer {...drawerProps}>
          <div style={{ height: '100%' }} ref={leftDrawerRef}>
            <Flex
              outset={{ vertical: 'x4' }}
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
            >
              <Icon name="promptings_logo_full_gradient" size={200} />
              <Flex flexDirection="row" columnGap="x_25">
                <Text
                  content="Powered by"
                  type="body"
                  isItalic
                  lineHeight={1.5}
                  style={{ fontSize: '11px', color: '#6B7280' }}
                />
                <Icon name="soc_logo" size={120} color="#000" />
              </Flex>
            </Flex>
            <Flex
              width="100%"
              height="100%"
              alignItems="center"
              flexDirection="column"
              inset="x1"
            >
              {recipientDrawerContent}
            </Flex>
          </div>
        </Drawer>
      )}
      {shouldPortalControls && (
        <Flex
          outset={{ top: 'x4' }}
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <ThreeDCardPreview
            cardType={digitalCard.card.type}
            orientation={
              digitalCard.card.isHorizontal ? 'LANDSCAPE' : 'PORTRAIT'
            }
            panels={mapPanelImages(
              digitalCard.card.frontPreviewUrl,
              digitalCard.card.insidePreviewUrl,
              digitalCard.card.backPreviewUrl,
              digitalCard.card.flapPreviewUrl,
              !!digitalCard.card.flapPreviewUrl,
              digitalCard.card.isHorizontal,
            )}
            height="calc(100dvh - 350px)"
            control={{
              type: 'Portal',
              portalId: 'controls3DCard',
              panelNames: controlPanelNames,
              shouldOnlyShowDropdown: !isDesktop,
            }}
            bgGradientColor={{ shouldDisplayBg: true }}
            isBackPanelLandscape={
              digitalCard.card.isHorizontal &&
              getIsBackPanelLandscape(digitalCard.card.panels)
            }
            shouldDisplayScene={canViewEnvelopeAnimation}
            giftCard={giftCardPreview}
          />
        </Flex>
      )}
      <div
        style={{
          marginTop: 'auto',
          marginBottom: '1rem',
          width: '100%',
          padding: '0 1rem',
          zIndex: 8,
          position: 'relative',
        }}
      >
        <div
          ref={controlContainerRef}
          id="controls3DCard"
          style={{
            margin: `0 auto ${isDesktop ? '2.5rem' : '7rem'}`,
            maxWidth: '500px',
          }}
        />
      </div>
      {!isDesktop && (
        <BottomSheet
          open={isRecipientDrawerOpen && !shouldShowRedeemCode}
          defaultSnap={185}
          blocking={false}
        >
          <div style={{ padding: '0px 16px' }}>{recipientDrawerContent}</div>
        </BottomSheet>
      )}
      {shouldShowRedeemCode && redeemCodeContent}
      {shouldShowRequestCard &&
        digitalCard.recipients.length > 0 &&
        digitalCard.recipients[0].contactRequest && (
          <Dialog
            padding=" 32px 8px 24px 8px"
            isOpen={shouldShowRequestCard}
            sheetBgColorOverride="white"
            onClose={() => {
              setShouldShowRequestCard(false)
              actions.setRecipientDrawerOpen(true)
            }}
            wrapperStyle={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ActRequestCard
              digitalCardToken={digitalCard.recipients[0].token}
              contactRequestToken={
                digitalCard.recipients[0].contactRequest.token
              }
              cardImgUrl={digitalCard.card.frontPreviewUrl}
              onBack={() => {
                setShouldShowRequestCard(false)
                actions.setRecipientDrawerOpen(true)
              }}
            />
          </Dialog>
        )}
      {shouldShowFreeCardUpsale && digitalCard && (
        <UpsaleDialog
          isOpen={shouldShowFreeCardUpsale}
          title={freeCardUpsaleContent?.title ?? 'marketing Paragraph title'}
          heroImage={freeCardHeroImage}
          description={
            freeCardUpsaleContent?.content ?? 'marketing paragraph description'
          }
          onClose={() => setShouldShowFreeCardUpsale(false)}
          primaryAction={{
            title:
              !!account && !account.isQualifiedForFirstFreeCard
                ? 'Send'
                : 'Send Free Card',
            onClick: () => {
              setShouldShowFreeCardUpsale(false)
              if (!!account) {
                actions.openCatalog(undefined, { digitalSendSelected: '' })
              } else {
                setShouldShowSendFreeCardFlow(true)
              }
            },
          }}
        />
      )}
      {shouldShowSendFreeCardFlow && (
        <ActSendFreeCardDialog
          onClose={() => {
            setShouldShowSendFreeCardFlow(false)
          }}
          onFinished={() => {
            setShouldShowSendFreeCardFlow(false)
            actions.openCatalog(undefined, { digitalSendSelected: '' })
          }}
        />
      )}
    </div>
  )
}

export default ViewAct
